import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Download, Loader, FileText, Layers } from 'lucide-react';
import { caseSummaryApi } from '../services/caseSummaryApi';
import { bundleApi } from '../services/bundleApi';

interface DownloadsButtonProps {
  caseId: string;
  hasMetadata: boolean;
}

/**
 * Unified Downloads dropdown shown in the case detail header. Both the
 * paginated bundle and the case summary are auto-generated by the analysis
 * pipeline — there are no triggers; this button only surfaces what's ready.
 *
 * The dropdown lists each artefact as it becomes available. While generation
 * is in flight, polling continues every few seconds and a "Preparing…" item
 * is shown for the missing format.
 */
export const DownloadsButton: React.FC<DownloadsButtonProps> = ({ caseId, hasMetadata }) => {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: summary } = useQuery(
    ['caseSummary', caseId],
    () => caseSummaryApi.getForCase(caseId),
    {
      enabled: !!hasMetadata,
      refetchInterval: (data) =>
        !data || data.status === 'GENERATING' ? 4000 : false,
      retry: false,
    }
  );

  const { data: bundle } = useQuery(
    ['paginatedBundle', caseId],
    () => bundleApi.getLatest(caseId),
    {
      enabled: !!hasMetadata,
      refetchInterval: (data) =>
        !data || data.status === 'GENERATING' ? 4000 : false,
      retry: false,
    }
  );

  // Whether the bundle is even available for this case. Cases uploaded before
  // sub-document detection moved upstream return ready=false; we hide the
  // bundle row entirely rather than show a perpetual "Preparing…".
  const { data: bundleAvailability } = useQuery(
    ['paginatedBundleAvailability', caseId],
    () => bundleApi.availability(caseId),
    {
      enabled: !!hasMetadata,
      retry: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleDownload = useCallback(async (kind: 'bundle' | 'summary-pdf' | 'summary-docx') => {
    setDownloading(kind);
    setOpen(false);
    try {
      if (kind === 'bundle') {
        await bundleApi.download(caseId);
      } else if (kind === 'summary-pdf') {
        await caseSummaryApi.download(caseId, 'pdf');
      } else {
        await caseSummaryApi.download(caseId, 'docx');
      }
    } catch (e) {
      console.error('Download failed', e);
    } finally {
      setDownloading(null);
    }
  }, [caseId]);

  if (!hasMetadata) return null;

  const summaryReady = summary?.status === 'READY';
  const summaryGenerating = summary?.status === 'GENERATING' || !summary;
  const bundleReady = bundle?.status === 'READY';
  const bundleGenerating = bundle?.status === 'GENERATING' || !bundle;
  // Hide the bundle row entirely for legacy cases that predate upstream
  // sub-document detection. We default to true while the availability call
  // is in flight so the row doesn't flicker on first render.
  const bundleAvailable = bundleAvailability?.ready ?? true;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={downloading !== null}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-medical-700 dark:text-medical-200 bg-white dark:bg-medical-800 border border-medical-200 dark:border-medical-600 rounded-md hover:bg-medical-50 dark:hover:bg-medical-700 transition-colors"
        title="Download case outputs"
        aria-label="Download case outputs"
      >
        {downloading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-medical-800 rounded-md shadow-lg border border-medical-200 dark:border-medical-600 min-w-[240px] py-1">
          {/* Paginated Bundle — hidden entirely for cases uploaded before
              sub-document detection moved upstream (April 2026). */}
          {bundleAvailable && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-medical-400 dark:text-medical-500 font-semibold">
                Paginated Bundle
              </div>
              {bundleReady ? (
                <button
                  type="button"
                  onClick={() => handleDownload('bundle')}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-medical-700 dark:text-medical-200 hover:bg-medical-50 dark:hover:bg-medical-700 transition-colors"
                >
                  <Layers className="w-3.5 h-3.5 text-medical-400" />
                  <span>PDF{bundle?.totalPages ? ` · ${bundle.totalPages}pp` : ''}</span>
                </button>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-medical-400 dark:text-medical-500">
                  <Loader className="w-3 h-3 animate-spin" />
                  <span>{bundleGenerating ? 'Preparing…' : 'Unavailable'}</span>
                </div>
              )}
            </>
          )}

          {/* Case Summary */}
          <div className={`px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-medical-400 dark:text-medical-500 font-semibold ${bundleAvailable ? 'border-t border-medical-100 dark:border-medical-700 mt-1' : ''}`}>
            Case Summary
          </div>
          {summaryReady ? (
            <>
              <button
                type="button"
                onClick={() => handleDownload('summary-pdf')}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-medical-700 dark:text-medical-200 hover:bg-medical-50 dark:hover:bg-medical-700 transition-colors"
              >
                <FileText className="w-3.5 h-3.5 text-medical-400" />
                <span>PDF</span>
              </button>
              <button
                type="button"
                onClick={() => handleDownload('summary-docx')}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-medical-700 dark:text-medical-200 hover:bg-medical-50 dark:hover:bg-medical-700 transition-colors"
              >
                <FileText className="w-3.5 h-3.5 text-medical-400" />
                <span>DOCX</span>
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-medical-400 dark:text-medical-500">
              <Loader className="w-3 h-3 animate-spin" />
              <span>{summaryGenerating ? 'Preparing…' : 'Unavailable'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
